/**
 * Address-bar cleanup: Shopify decorates product URLs clicked from search results
 * (_pos/_sid/_ss/_psq/_v — Search & Discovery click attribution) and from product
 * recommendations (pr_prod_strat/pr_rec_id/pr_ref_pid/pr_seq), which pollutes the
 * URLs marketing copy from the address bar. The market selector's ?country=XX
 * switch parameter (snippets/localization-selector.liquid) also lingers after
 * Shopify has consumed it server-side (cookie written, domain resolved), so it is
 * cleaned too. Attribution is captured server-side by the decorated request and by
 * the tracking scripts that initialise during page load, so rewriting the address
 * AFTER load (DOMContentLoaded, replaceState) keeps the analytics intact while the
 * visible URL goes clean. Every other param (variant, page, filters, q) is
 * preserved; nothing happens when no tracking params are present.
 */
(function () {
	document.addEventListener('DOMContentLoaded', function () {
		const trackedParams = ['_pos', '_sid', '_ss', '_psq', '_v', '_psid', 'pr_prod_strat', 'pr_rec_id', 'pr_ref_pid', 'pr_seq', 'country'];
		const url = new URL(window.location.href);
		const found = trackedParams.filter(function (param) {
			return url.searchParams.has(param);
		});
		if (found.length === 0) return;

		found.forEach(function (param) {
			url.searchParams.delete(param);
		});
		history.replaceState(history.state, '', url.pathname + (url.search || '') + url.hash);
	});
})();

/**
 * Product card image navigation (mobile): the next arrow toggles between the
 * primary and secondary image (rendered when settings.card_image_nav is 'arrow' or
 * 'arrow_swipe', see snippets/product-card.liquid and the Graphene card function).
 * Delegated so it covers theme cards, Graphene-rendered cards and load-more without
 * re-init.
 *
 * In 'arrow_swipe' mode the collection and search grid cards wrap their images in a
 * snap carousel (product-card__swipe-area) so the image can also be swiped; there the arrow
 * scrolls the carousel to the other full image instead of toggling the opacity
 * classes, which would fight the carousel's side-by-side layout — and because
 * both the arrow and a swipe just move the same scroll position, they can never
 * get out of sync.
 */
document.addEventListener('click', function (event) {
	const button = event.target.closest('.js-show-next-btn');
	if (!button) return;

	const figure = button.closest('.product-card__figure');
	if (!figure) return;

	const carousel = figure.querySelector('.product-card__swipe-area');
	if (carousel && carousel.scrollWidth > carousel.clientWidth) {
		const atPrimary = carousel.scrollLeft < carousel.clientWidth / 2;
		carousel.scrollTo({ left: atPrimary ? carousel.clientWidth : 0, behavior: 'smooth' });
		return;
	}

	figure.classList.toggle('show-secondary');
	figure.classList.toggle('show-primary', !figure.classList.contains('show-secondary'));
});

/**
 * Recent searches (search panel empty state): submitted terms are kept in
 * localStorage (deduped, newest first, capped at 6) and rendered into the
 * panel's Recent Searches rail on the next page load.
 */
(function () {
	const KEY = 'la:recent-searches';

	function readRecent() {
		try {
			return JSON.parse(localStorage.getItem(KEY)) || [];
		} catch (e) {
			return [];
		}
	}

	function renderRecent() {
		const wrapper = document.querySelector('[data-recent-searches]');
		if (!wrapper) return;

		const terms = readRecent();
		if (!terms.length) return;

		const list = wrapper.querySelector('[data-recent-searches-list]');
		list.replaceChildren(...terms.map(function (term) {
			const link = document.createElement('a');
			link.className = 'link-faded-reverse text-sm';
			link.href = (window.Shopify?.routes?.root || '/') + 'search?q=' + encodeURIComponent(term);
			link.textContent = term;
			return link;
		}));
		wrapper.removeAttribute('hidden');
	}

	document.addEventListener('submit', function (event) {
		const form = event.target.closest('#predictive-search-form');
		if (!form) return;

		// the prefix-wildcard appended for partial matching is an implementation
		// detail — recents store what the visitor actually typed
		const term = (form.elements.q && form.elements.q.value || '').trim().replace(/\*+$/, '');
		if (!term) return;

		try {
			const terms = readRecent().filter(function (t) { return t.toLowerCase() !== term.toLowerCase(); });
			terms.unshift(term);
			localStorage.setItem(KEY, JSON.stringify(terms.slice(0, 6)));
		} catch (e) { /* storage unavailable — skip silently */ }
	}, true);

	document.addEventListener('DOMContentLoaded', renderRecent);
})();

/**
 * Prefix search on the results page: Shopify's /search matches whole words only,
 * while the predictive panel prefix-matches the last typed word — so "ma" finds
 * MAXINE in the panel but nothing on the page. Navigating with Shopify's trailing
 * wildcard appended to the last word gives the page the panel's behaviour. The
 * wildcard lives only in the URL: the native submit is cancelled and replaced
 * with a manual navigation, so the visible input keeps exactly what was typed.
 * Covers the header search and the search page's own form. Skipped for advanced
 * queries (quotes, field prefixes, operators) so power users keep exact syntax.
 * Display spots strip the asterisk again. Capture phase, registered after the
 * recents handler so recents store the raw term.
 */
document.addEventListener('submit', function (event) {
	const form = event.target.closest('#predictive-search-form, .main-search-form');
	if (!form || !form.elements.q) return;

	const term = form.elements.q.value.trim();
	if (!term) return;
	if (/["':*()]/.test(term)) return;
	if (/(^|\s)-/.test(term)) return;
	if (/\b(AND|OR|NOT)\b/.test(term)) return;
	if (!/[\p{L}\p{N}]$/u.test(term)) return;

	event.preventDefault();

	const params = new URLSearchParams(new FormData(form));
	params.set('q', term + '*');
	window.location.assign(form.action.split('?')[0] + '?' + params.toString());
}, true);

/**
 * Applied-filter pills inside the facets DRAWER: a pill click must not trigger the
 * facet-link section re-render (that would replace the open drawer). Instead the
 * matching form inputs are unchecked/cleared client-side — the removal then applies
 * together with any other pending changes when the drawer closes, which is the
 * drawer's native apply-on-close behaviour. In the desktop sidebar the pills keep
 * their facet-link live update, so this handler only acts inside facets-drawer.
 * Capture phase so it beats the facet-link element's own click listener.
 */
document.addEventListener('click', function (event) {
	const pill = event.target.closest('[data-applied-pill], [data-applied-clear-all]');
	if (!pill || !pill.closest('facets-drawer')) return;

	event.preventDefault();
	event.stopPropagation();

	const form = pill.closest('form[is="facets-form"]');
	if (!form) return;

	function clearInput(input) {
		if (input.type === 'checkbox' || input.type === 'radio') {
			if (!input.checked) return;
			input.checked = false;
		} else {
			if (input.value === '') return;
			input.value = '';
		}
		input.dispatchEvent(new Event('change', { bubbles: true }));
	}

	if (pill.hasAttribute('data-applied-clear-all')) {
		form.querySelectorAll('input[name^="filter."]').forEach(clearInput);
		pill.closest('[data-facets-applied]')?.setAttribute('hidden', '');
		return;
	}

	const param = pill.dataset.param;
	const paramMax = pill.dataset.paramMax;
	const value = pill.dataset.value;

	form.querySelectorAll('input[name="' + param + '"]' + (paramMax ? ', input[name="' + paramMax + '"]' : '')).forEach(function (input) {
		if (paramMax || input.value === value) {
			clearInput(input);
		}
	});

	pill.setAttribute('hidden', '');
	const applied = pill.closest('[data-facets-applied]');
	if (applied && !applied.querySelector('[data-applied-pill]:not([hidden])')) {
		applied.setAttribute('hidden', '');
	}
}, true);

/**
 * Back to top (collection pages): the button rendered by main-collection.liquid is
 * revealed once the user scrolls past the configured number of product rows, and
 * scrolls smoothly to the top on click. Threshold recomputes on resize and after
 * load-more appends (section:rendered).
 */
(function () {
	const backToTopButton = document.querySelector('[data-back-to-top]');
	if (!backToTopButton) return;

	const rowsBeforeShowing = parseInt(backToTopButton.dataset.rows, 10) || 5;
	let threshold = Infinity;
	let tickPending = false;

	function computeThreshold() {
		const grid = document.querySelector('.product-list');
		const card = grid && grid.querySelector('product-card, .product-card');

		if (!card) {
			threshold = Infinity;
			return;
		}

		const rowGap = parseFloat(getComputedStyle(grid).rowGap) || 0;
		const gridTop = grid.getBoundingClientRect().top + window.scrollY;
		threshold = gridTop + rowsBeforeShowing * (card.offsetHeight + rowGap);
	}

	function updateVisibility() {
		backToTopButton.classList.toggle('back-to-top--visible', window.scrollY > threshold);
	}

	function recompute() {
		computeThreshold();
		updateVisibility();
	}

	backToTopButton.addEventListener('click', function () {
		window.scrollTo({ top: 0, behavior: 'smooth' });
	});

	document.addEventListener('DOMContentLoaded', recompute);
	window.addEventListener('load', recompute);
	window.addEventListener('resize', recompute, { passive: true });
	document.addEventListener('section:rendered', recompute);

	window.addEventListener('scroll', function () {
		if (tickPending) return;
		tickPending = true;
		requestAnimationFrame(function () {
			tickPending = false;
			updateVisibility();
		});
	}, { passive: true });
})();

/**
 * PLP position restore (load-more pagination): LoadMoreButton (theme.js) updates
 * the address to the loaded page's URL and records the loaded depth here; a
 * product-card click records the scroll position. Returning from a PDP:
 * - bfcache restores the full DOM natively (this code doesn't run — no DOMContentLoaded);
 * - a fresh re-render lands on ?page=N with only that page's products, so pages
 *   1..N-1 are fetched (section rendering, cards only — same mechanics as load
 *   more) and prepended in order, then the saved scroll position is restored.
 * State is keyed per path + filters (page param excluded), so filter or sort
 * changes never restore across views. Everything is inert on page 1, without
 * stored state, or when storage is unavailable.
 */
(function () {
	function storageKey() {
		const params = new URLSearchParams(window.location.search);
		params.delete('page');
		const qs = params.toString();
		return 'la:plp:' + window.location.pathname + (qs ? '?' + qs : '');
	}

	function readState() {
		try {
			return JSON.parse(sessionStorage.getItem(storageKey())) || {};
		} catch (e) {
			return {};
		}
	}

	function writeState(patch) {
		try {
			sessionStorage.setItem(storageKey(), JSON.stringify(Object.assign(readState(), patch)));
		} catch (e) { /* storage unavailable — restore simply won't happen */ }
	}

	// shared with LoadMoreButton in theme.js (guarded there, so load order is safe)
	window.laPlpState = { readState, writeState };

	// the departure point: any product-card link click inside a product grid
	document.addEventListener('click', function (event) {
		if (!event.target.closest('.product-list a[href*="/products/"]')) return;
		writeState({ y: window.scrollY });
	});

	document.addEventListener('DOMContentLoaded', async function () {
		const currentPage = parseInt(new URLSearchParams(window.location.search).get('page'), 10) || 1;
		if (currentPage <= 1) return;

		const productList = document.querySelector('.collection .product-list');
		const section = productList && productList.closest('.shopify-section');
		const state = readState();
		if (!productList || !section || !state.pages || state.pages < currentPage) return;

		const sectionId = section.id.replace('shopify-section-', '');
		const pageUrl = new URL(window.location.href);
		pageUrl.searchParams.set('section_id', sectionId);
		const insertAnchor = productList.firstElementChild;

		for (let page = 1; page < currentPage; page++) {
			pageUrl.searchParams.set('page', String(page));
			try {
				const responseText = await (await fetch(pageUrl)).text();
				const responseDocument = new DOMParser().parseFromString(responseText, 'text/html');
				responseDocument.querySelectorAll('product-list > product-card').forEach(function (card) {
					card.removeAttribute('reveal-on-scroll');
					productList.insertBefore(card, insertAnchor);
				});
			} catch (e) {
				return; // partial restore beats a broken one — leave what rendered
			}
		}

		if (typeof state.y === 'number') {
			window.scrollTo(0, state.y);
		}
	});
})();

/**
 * Auto availability filter (collection/search faceting): while any real filter is
 * applied, "In stock only" (filter.v.availability=1) is applied with it, so size and
 * other option filters read stock-aware; when the last real filter is removed the
 * automation removes availability again. The moment the shopper touches the
 * availability control themselves — the drawer checkbox or its applied pill — the
 * automation stands down and their explicit choice rules, until all filters are
 * cleared (which re-arms it). Implemented as a capture-phase rewrite of the
 * facet:update URL, the single choke point every filter path flows through (form
 * submits, sidebar changes, pills, clear all, sort, pagination) — the bubble-phase
 * theme handler then fetches the adjusted URL, so there are no extra renders.
 */
(function () {
	const AVAILABILITY_PARAM = 'filter.v.availability';
	const MODE_KEY = 'la:availability-mode'; // 'auto' (default) | 'user'

	function getMode() {
		try {
			return sessionStorage.getItem(MODE_KEY) || 'auto';
		} catch (e) {
			return 'auto';
		}
	}

	function setMode(mode) {
		try {
			sessionStorage.setItem(MODE_KEY, mode);
		} catch (e) { /* storage unavailable — behaves as permanent 'auto' */ }
	}

	// Explicit user intent: touching the availability checkbox itself (isTrusted
	// excludes the synthetic change events our drawer pill intercept dispatches)...
	document.addEventListener('change', function (event) {
		const input = event.target;
		if (event.isTrusted && input.name === AVAILABILITY_PARAM && input.closest('form[is="facets-form"]')) {
			setMode('user');
		}
	}, true);

	// ...or removing the availability pill (page bar or drawer)
	document.addEventListener('click', function (event) {
		const pill = event.target.closest('[data-applied-pill]');
		if (pill && pill.dataset.param === AVAILABILITY_PARAM) {
			setMode('user');
		}
	}, true);

	document.addEventListener('facet:update', function (event) {
		const url = event.detail && event.detail.url;
		if (!url || !url.searchParams) return;
		const params = url.searchParams;

		const hasOtherFilters = Array.from(params.entries()).some(function (entry) {
			return entry[0].indexOf('filter.') === 0 && entry[0] !== AVAILABILITY_PARAM && entry[1] !== '';
		});

		// Fully cleared view (no filters of any kind): the automation re-arms, so a
		// past manual choice doesn't linger into the shopper's next filtering session
		if (!hasOtherFilters && !params.has(AVAILABILITY_PARAM)) {
			setMode('auto');
			return;
		}

		if (getMode() !== 'auto') return;

		if (hasOtherFilters) {
			params.set(AVAILABILITY_PARAM, '1');
		} else {
			params.delete(AVAILABILITY_PARAM);
		}
	}, true);
})();

/**
 * Mobile menu: one accordion open at a time. When a menu accordion opens, any
 * open sibling in the same link list is closed via its summary click so the
 * theme's accordion-disclosure closing animation still runs. Capture phase
 * because the toggle event does not bubble.
 */
document.addEventListener('toggle', function (event) {
	const details = event.target;
	if (!details.matches || !details.matches('.header-sidebar__linklist details[is="accordion-disclosure"][open]')) return;

	const list = details.closest('.header-sidebar__linklist');
	list.querySelectorAll('details[is="accordion-disclosure"][open]').forEach(function (other) {
		if (other !== details && !other.contains(details) && !details.contains(other)) {
			other.querySelector('summary')?.click();
		}
	});
}, true);
